import { action, computed, makeObservable, observable, onBecomeObserved, runInAction } from "mobx";
import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import type { AppStore } from "@/store";
import type { IZapierIntegrationStore } from "@/store/zapier-integration/types";

export class AppStoreZapierIntegrationStore implements IZapierIntegrationStore {
  private ready = false;
  private loading = false;
  private disabling = false;
  private settings: { isEnabled: boolean } = { isEnabled: false };

  constructor(private readonly store: AppStore) {
    makeObservable<AppStoreZapierIntegrationStore, "ready" | "loading" | "disabling" | "settings">(
      this,
      {
        isEnabled: computed,
        settings: observable,

        // Load
        load: action,
        loading: observable,
        isLoading: computed,

        // Reload
        reload: action,

        // Ready
        ready: observable,
        isReady: computed,

        // Disable
        disable: action,
        disabling: observable,
        isDisabling: computed,
      }
    );

    onBecomeObserved(this, "settings", this.load);
  }

  get isReady() {
    /**
     * Using this just so we can observe `settings` and force load...
     * @MacroMackie will clean this up in the future.
     */
    if (this.settings.isEnabled) {
      return true;
    }

    return this.ready;
  }

  get isLoading() {
    return this.loading;
  }

  get isDisabling() {
    return this.disabling;
  }

  get isEnabled() {
    return this.settings.isEnabled;
  }

  load = async () => {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await api.get("/v2/integrations/zapier/settings", {});

      runInAction(() => {
        const isEnabled = response.data?.is_enabled ?? false;

        this.settings = { isEnabled };
      });
    } catch (unknownError) {
      const error = unknownError as Error;

      logger.error({
        message: "Failed to load zapier integration settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.ready = true;
      });
    }
  };

  reload = async () => {
    this.load();
  };

  disable = async (): Promise<void> => {
    runInAction(() => {
      this.disabling = true;
    });

    try {
      const response = await api.post("/v2/integrations/zapier/disable", {});

      const data = response.data;

      if (!data || response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Failed to disable zapier integration"
        );
      }

      runInAction(() => {
        this.settings = { isEnabled: false };
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreZapierIntegrationStore] Failed to disable zapier integration",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      throw error;
    } finally {
      runInAction(() => {
        this.disabling = false;
      });
    }
  };
}
